import { useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { SelectInput } from "../../../components/SelectInput";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";

const COLUMN_MAPPING_VALUES = [
  "",
  "ID",
  "CARD_NUMBER",
  "INITIAL_AMOUNT",
  "REMAINING_AMOUNT",
  "VALID_UNTIL",
  "STATUS",
  "SKIP",
] as const;

const ColumnMappingsContainer = styled.div`
  margin-top: 0;
`;

const MappingList = styled.div`
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  overflow: hidden;
  background-color: ${colors.backgroundLight};
`;

const MappingHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x2}px;
  align-items: center;
  padding: ${spacing.x2}px ${spacing.x3}px;
  background-color: ${colors.backgroundDark};
  border-bottom: 1px solid ${colors.border};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${colors.textMuted};
`;

const ColumnMappingRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.x2}px;
  align-items: center;
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-bottom: 1px solid ${colors.border};
  background-color: ${colors.backgroundLight};

  &:last-child {
    border-bottom: none;
  }
`;

const ColumnName = styled.div`
  font-weight: 500;
  color: ${colors.text};
  font-size: 14px;
`;

interface GiftcardColumnMappingProps {
  headers: string[];
  mappings: Record<number, string>;
  onMappingChange: (index: number, value: string) => void;
}

export const GiftcardColumnMapping = ({
  headers,
  mappings,
  onMappingChange,
}: GiftcardColumnMappingProps) => {
  const { t } = useTranslation();

  const selectOptions = useMemo(() => {
    return COLUMN_MAPPING_VALUES.map((value) => ({
      value,
      label: t(`giftcardImport.columnField.${value === "" ? "SKIP" : value}`),
    }));
  }, [t]);

  if (headers.length === 0) {
    return null;
  }

  return (
    <ColumnMappingsContainer>
      <MappingList>
        <MappingHeaderRow>
          <span>{t("giftcardImport.mappingTableSourceColumn")}</span>
          <span>{t("giftcardImport.mappingTableTargetField")}</span>
        </MappingHeaderRow>
        {headers.map((header, index) => (
          <ColumnMappingRow key={index}>
            <ColumnName>{header}</ColumnName>
            <SelectInput
              value={mappings[index] || ""}
              onChange={(e) => onMappingChange(index, e.target.value)}
            >
              {selectOptions.map((opt) => (
                <option key={opt.value || "SKIP"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </SelectInput>
          </ColumnMappingRow>
        ))}
      </MappingList>
    </ColumnMappingsContainer>
  );
};
